/**
 * component-amp-cart.js
 *
 * Enhances the SlideCart HQ (AMP) cart drawer's tiered-rewards progress bar.
 *
 * The app renders a progress bar like:
 *   <div data-testid="TieredRewardsProgressBar">
 *     <div class="rewards-progress-bar">
 *       <div class="rewards-progress" style="width: 94.89%;"></div>
 *       ...
 *     </div>
 *     <div class="rewards-tiers-labels">
 *       ...
 *       <span class="rewards-tiers-labels-item-amount">$59.00</span>
 *       <span class="rewards-tiers-labels-item-label">FREE shipping</span>
 *     </div>
 *   </div>
 *
 * The Figma design wants a pill floating at the tip of the filled (orange)
 * progress that shows the REMAINING amount to the goal (e.g. "42.99").
 *
 * The app re-renders this markup every time an item is added/removed, so we:
 *   1. Observe the cart DOM with a MutationObserver.
 *   2. On every relevant change, (re)compute the remaining amount and inject
 *      / update a single pill anchored to the progress tip.
 *
 * The remaining amount is derived from the goal amount label and the fill
 * percentage, so it stays correct without needing the app's internal data.
 *
 * It also restructures each cart line item to match the Figma design:
 *   - splits the "Flavor • Servings" text into a green flavor pill + a
 *     separate servings line, and
 *   - moves the price up onto the title row (top-right).
 * All item changes are idempotent and re-applied on every cart re-render.
 */
(function () {
  'use strict';

  var PILL_CLASS = 'amp-rewards-remaining-pill';
  var BAR_SELECTOR = '[data-testid="TieredRewardsProgressBar"]';

  /**
   * Parse a currency string like "$59.00" / "£1,234.50" into a number.
   * Returns NaN when it can't find a number.
   */
  function parseAmount(text) {
    if (!text) return NaN;
    // Keep digits, dot and comma, then strip thousands separators.
    var cleaned = String(text).replace(/[^0-9.,]/g, '');
    if (!cleaned) return NaN;

    // If both separators present, assume "," is thousands and "." is decimal.
    if (cleaned.indexOf('.') > -1 && cleaned.indexOf(',') > -1) {
      cleaned = cleaned.replace(/,/g, '');
    } else if (cleaned.indexOf(',') > -1 && cleaned.indexOf('.') === -1) {
      // Only commas: treat the last comma as a decimal separator.
      cleaned = cleaned.replace(/,(\d{1,2})$/, '.$1').replace(/,/g, '');
    }
    return parseFloat(cleaned);
  }

  /**
   * Format the remaining amount for the pill: thousands separators, no
   * currency symbol, and no trailing zeros (3.01 -> "3.01", 5.00 -> "5",
   * 5.50 -> "5.5").
   */
  function formatAmount(value) {
    // Round to 2 decimals, then drop any trailing zeros / dangling dot.
    var rounded = (Math.round(value * 100) / 100).toFixed(2);
    rounded = rounded.replace(/\.?0+$/, '');
    var parts = rounded.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  /**
   * Build / update the remaining-amount pill for a single progress bar.
   */
  function updateBar(bar) {
    var fill = bar.querySelector('.rewards-progress');
    var goalEl = bar.querySelector('.rewards-tiers-labels-item-amount');
    var track = bar.querySelector('.rewards-progress-bar');
    if (!fill || !goalEl || !track) return;

    var goal = parseAmount(goalEl.textContent);
    var pct = parseFloat((fill.style.width || '').replace('%', ''));
    if (isNaN(goal) || isNaN(pct)) return;

    var existing = track.querySelector('.' + PILL_CLASS);

    // Goal reached -> nothing remaining, remove any leftover pill.
    if (pct >= 100) {
      if (existing) existing.parentNode.removeChild(existing);
      return;
    }

    // remaining = goal * (1 - pct/100). Derived from goal + fill percentage
    // so we don't depend on the app's internal cart-total value.
    var remaining = goal * (1 - pct / 100);
    if (remaining < 0) remaining = 0;

    var label = formatAmount(remaining);

    // Reformat the app's goal label too (drop trailing zeros: $59.00 -> $59).
    reformatGoalLabel(goalEl);

    var pill = existing;
    if (!pill) {
      pill = document.createElement('span');
      pill.className = PILL_CLASS;
      // The track is the positioning context for the pill.
      if (getComputedStyle(track).position === 'static') {
        track.style.position = 'relative';
      }
      track.appendChild(pill);
    }

    // Only touch the DOM when the value actually changed.
    if (pill.textContent !== label) pill.textContent = label;

    positionPill(track, pill, pct);
  }

  /**
   * Place the pill so its RIGHT edge sits at the orange fill's tip, clamped so
   * it never spills off either end of the track.
   *
   *   left = clamp(0, fillTip - pillWidth, trackWidth - pillWidth)
   *
   * Pixel math needs a laid-out track. When the drawer is hidden the track
   * measures 0px, so we retry on the next frame until it has a real width.
   */
  function positionPill(track, pill, pct) {
    pill.style.transform = 'translateY(-50%)';

    var trackWidth = track.clientWidth;
    var pillWidth = pill.offsetWidth;

    // Not laid out yet (hidden drawer) -> try again next frame, don't guess.
    if (trackWidth === 0 || pillWidth === 0) {
      requestAnimationFrame(function () {
        // The pill may have been removed/re-rendered in the meantime.
        if (pill.isConnected) positionPill(track, pill, pct);
      });
      return;
    }

    var fillTip = (pct / 100) * trackWidth;
    var left = fillTip - pillWidth;          // right edge at the fill tip
    var max = trackWidth - pillWidth;        // never past the right edge
    if (left < 0) left = 0;                   // never past the left edge
    if (left > max) left = max;

    pill.style.left = left + 'px';
  }

  /**
   * Strip trailing zeros from the app's goal amount label, preserving its
   * currency symbol (e.g. "$59.00" -> "$59", "£12.50" -> "£12.5").
   */
  function reformatGoalLabel(goalEl) {
    var raw = goalEl.textContent;
    if (!raw) return;
    // Already cleaned? bail to avoid re-running on every mutation.
    if (goalEl.getAttribute('data-amp-formatted') === raw) return;

    var symbolMatch = raw.match(/^[^\d]*/);
    var symbol = symbolMatch ? symbolMatch[0] : '';
    var amount = parseAmount(raw);
    if (isNaN(amount)) return;

    var formatted = symbol + formatAmount(amount);
    if (formatted !== raw) {
      goalEl.textContent = formatted;
    }
    goalEl.setAttribute('data-amp-formatted', formatted);
  }

  /**
   * Split a line item's variant text ("Mandarin • 50 Servings") into a green
   * flavor pill plus a separate servings line, matching the Figma layout.
   *
   * The app renders both parts joined by a bullet in one <p class="variants-p">.
   * We restructure it into:
   *   <p class="variants-p" data-amp-split="1">
   *     <span class="amp-flavor-pill">Mandarin</span>
   *     <span class="amp-servings">50 Servings</span>
   *   </p>
   * Guarded with data-amp-split so re-renders don't double-wrap.
   */
  function enhanceVariants(p, variantId) {
    // The app re-renders items wholesale, so a freshly rendered <p> won't have
    // our marker even if a previous one did. Read the raw text only when unsplit.
    if (p.getAttribute('data-amp-split') === '1') return;

    var raw = (p.textContent || '').trim();
    if (!raw) return;

    // Split on the bullet separator the app uses (•). Fall back gracefully if
    // there's only one segment (no servings, or a different separator).
    var segments = raw.split('•').map(function (s) { return s.trim(); }).filter(Boolean);
    if (!segments.length) return;

    var flavor = segments[0];
    var rest = segments.slice(1).join(' • ');

    // Rebuild the contents.
    p.textContent = '';

    var flavorPill = document.createElement('span');
    flavorPill.className = 'amp-flavor-pill';
    flavorPill.textContent = flavor;

    // Colour the pill from the variant's custom.flavor_color metafield (mapped
    // in via window.AMP_FLAVOR_COLORS). Leave the CSS default when unset.
    // Use 'important' so it beats the !important default rule in the CSS.
    var color = flavorColor(variantId);
    if (color) flavorPill.style.setProperty('background', color, 'important');

    p.appendChild(flavorPill);

    if (rest) {
      var servings = document.createElement('span');
      servings.className = 'amp-servings';
      servings.textContent = rest;
      p.appendChild(servings);
    }

    p.setAttribute('data-amp-split', '1');
  }

  /**
   * Rearrange an item's controls to match the Figma layout:
   *   - price moves UP into the title row (.main-top), top-right;
   *   - the remove button moves DOWN into .main-bottom, so it sits bottom-right
   *     next to the quantity selector.
   * The app renders price in .main-bottom and the remove button in .main-top,
   * so we swap them. Both moves are idempotent.
   */
  function repositionControls(item) {
    var mainTop = item.querySelector('.main-top');
    var mainBottom = item.querySelector('.main-bottom');
    var price = item.querySelector('.price-discount-block');
    var remove = item.querySelector('.remove');

    if (mainTop && price && price.parentNode !== mainTop) {
      mainTop.appendChild(price);
    }
    if (mainBottom && remove && remove.parentNode !== mainBottom) {
      mainBottom.appendChild(remove);
    }
  }

  /**
   * Look up a variant's flavor pill colour from the Liquid-rendered map
   * (window.AMP_FLAVOR_COLORS, keyed by variant id). Returns null when unset
   * so the CSS default colour applies.
   */
  function flavorColor(variantId) {
    if (!variantId) return null;
    var map = window.AMP_FLAVOR_COLORS;
    if (!map) return null;
    var color = map[variantId];
    return color || null;
  }

  /**
   * Apply all per-line-item enhancements.
   */
  function enhanceItems() {
    var items = document.querySelectorAll('.item');
    for (var i = 0; i < items.length; i++) {
      var variantId = items[i].getAttribute('data-variant-id');
      var p = items[i].querySelector('.variants-p');
      if (p) enhanceVariants(p, variantId);
      repositionControls(items[i]);
    }
  }

  /**
   * Wrap the discount box in an accordion so it collapses by default.
   * The accordion header shows "Add Promo Code" with a chevron toggle.
   */
  function wrapDiscountAccordion() {
    var discountBox = document.querySelector('.discount-box');
    if (!discountBox) return;
    // Already wrapped? Skip.
    if (discountBox.getAttribute('data-amp-accordion') === '1') return;

    // Create accordion wrapper
    var accordion = document.createElement('div');
    accordion.className = 'amp-discount-accordion';

    // Create header button
    var header = document.createElement('button');
    header.type = 'button';
    header.className = 'amp-discount-accordion__header';
    header.setAttribute('aria-expanded', 'false');

    var headerText = document.createElement('span');
    headerText.textContent = 'Add Promo Code';

    // Chevron SVG
    var chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    chevron.setAttribute('class', 'amp-discount-accordion__chevron');
    chevron.setAttribute('width', '20');
    chevron.setAttribute('height', '20');
    chevron.setAttribute('viewBox', '0 0 20 20');
    chevron.setAttribute('fill', 'none');
    chevron.innerHTML = '<path d="M5 7.5L10 12.5L15 7.5" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>';

    header.appendChild(headerText);
    header.appendChild(chevron);

    // Create body container
    var body = document.createElement('div');
    body.className = 'amp-discount-accordion__body';

    // Move discount box contents into body
    while (discountBox.firstChild) {
      body.appendChild(discountBox.firstChild);
    }

    // Assemble accordion
    accordion.appendChild(header);
    accordion.appendChild(body);

    // Replace discount box content with accordion
    discountBox.appendChild(accordion);
    discountBox.setAttribute('data-amp-accordion', '1');

    // Toggle handler
    header.addEventListener('click', function () {
      var isOpen = accordion.classList.toggle('is-open');
      header.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  /**
   * Restructure upsell items to move the price above the Add button.
   * The app renders price in .upsell-text-prices; we move it into .upsell-add
   * so it sits directly above the Add button on the right.
   */
  function enhanceUpsellItems() {
    var upsells = document.querySelectorAll('.upsell-item');
    for (var i = 0; i < upsells.length; i++) {
      var item = upsells[i];
      if (item.getAttribute('data-amp-upsell-enhanced') === '1') continue;

      // Find the price element by data-testid
      var price = item.querySelector('[data-testid="AupsellPrice"]');
      var addContainer = item.querySelector('.upsell-add');

      if (price && addContainer && price.parentNode !== addContainer) {
        // Move price to the top of add container (above the Add button)
        addContainer.insertBefore(price, addContainer.firstChild);
      }

      item.setAttribute('data-amp-upsell-enhanced', '1');
    }
  }

  /**
   * Keep the discount box + footer from jumping during a cart re-render.
   *
   * The app destroys and recreates the .upsells node on every cart change.
   * While it's gone, everything below it (discount box, footer) slides up to
   * fill the gap, then drops back down when the new upsells render — the
   * "float above then settle" flicker.
   *
   * Fix: remember the upsells' rendered height, and the instant the node
   * disappears, reserve that exact height on the cart's stable scroll body
   * (the .upsells parent, which survives the re-render) via a CSS variable +
   * a zero-cost ::after spacer. The moment the new upsells node is measured we
   * clear the reservation in the same frame. Nothing below ever moves, and
   * there is no timer or transition to get out of sync.
   */
  var UPSELLS_SELECTOR = '#slidecarthq .upsells';
  var RESERVE_ATTR = 'data-amp-upsells-reserve';
  var RESERVE_VAR = '--amp-upsells-reserve';
  var lastUpsellsHeight = 0;
  // Stable ancestor that holds the reservation while .upsells is detached.
  var reserveHost = null;

  function getUpsells() {
    return document.querySelector(UPSELLS_SELECTOR);
  }

  // Remember the upsells height + its parent so we can reserve space the
  // instant the node is removed. Call this BEFORE the re-render mutates the DOM.
  function captureUpsellsHeight() {
    var upsells = getUpsells();
    if (upsells && upsells.offsetHeight > 0) {
      lastUpsellsHeight = upsells.offsetHeight;
      if (upsells.parentNode) reserveHost = upsells.parentNode;
    }
  }

  function reserveUpsellsSpace() {
    if (!reserveHost || lastUpsellsHeight <= 0) return;
    reserveHost.style.setProperty(RESERVE_VAR, lastUpsellsHeight + 'px');
    reserveHost.setAttribute(RESERVE_ATTR, '1');
  }

  function clearUpsellsReservation() {
    if (!reserveHost) return;
    reserveHost.style.removeProperty(RESERVE_VAR);
    reserveHost.removeAttribute(RESERVE_ATTR);
  }

  // Reconcile the reservation against the live DOM. Runs after every update:
  //  - upsells present  -> refresh remembered height, drop any reservation.
  //  - upsells missing   -> reserve the last-known height so the footer holds.
  function syncUpsellsReservation() {
    var upsells = getUpsells();
    if (upsells && upsells.offsetHeight > 0) {
      lastUpsellsHeight = upsells.offsetHeight;
      if (upsells.parentNode) reserveHost = upsells.parentNode;
      clearUpsellsReservation();
    } else if (lastUpsellsHeight > 0) {
      reserveUpsellsSpace();
    }
  }

  /**
   * Update the checkout button to show the order total.
   * Changes "Checkout" to "CHECK OUT - $XX.XX"
   * Also adds text above and below the button.
   */
  function updateCheckoutButton() {
    var form = document.querySelector('#slidecart-checkout-form');
    var button = form ? form.querySelector('button[name="checkout"]') : null;
    var subtotal = document.querySelector('.slidecart-subtotal');

    if (button && subtotal) {
      var amount = subtotal.textContent.trim();
      var newText = 'CHECK OUT - ' + amount;
      // Only update if different to avoid unnecessary DOM changes
      if (button.textContent !== newText) {
        button.textContent = newText;
      }
    }

    // Add text below the checkout button
    if (form && !form.getAttribute('data-amp-checkout-enhanced')) {
      // Text below button
      var belowText = document.createElement('p');
      belowText.className = 'amp-checkout-below-text';
      belowText.textContent = 'Shipping & Taxes Calculated At Checkout';
      form.appendChild(belowText);

      form.setAttribute('data-amp-checkout-enhanced', '1');
    }
  }

  /**
   * Update the subtotal label to include item count.
   * Changes "Subtotal" to "SUBTOTAL (X ITEM)" or "SUBTOTAL (X ITEMS)"
   */
  function updateSubtotalLabel() {
    var cartCount = document.querySelector('.cart-count');
    var subtotalLabel = document.querySelector('.subtotal-label span');

    if (cartCount && subtotalLabel) {
      var countText = cartCount.textContent.replace(/[()]/g, '').trim();
      var count = parseInt(countText, 10) || 0;
      var itemText = count === 1 ? 'ITEM' : 'ITEMS';
      var newText = 'SUBTOTAL (' + count + ' ' + itemText + ')';
      // Only update if different to avoid unnecessary DOM changes
      if (subtotalLabel.textContent !== newText) {
        subtotalLabel.textContent = newText;
      }
    }
  }

  /**
   * Fetch collection image URL via AJAX. Returns a promise that resolves to the image URL or null.
   * Caches results in window.AMP_COLLECTION_IMAGES for subsequent calls.
   */
  function fetchCollectionImage(handle) {
    window.AMP_COLLECTION_IMAGES = window.AMP_COLLECTION_IMAGES || {};
    
    if (window.AMP_COLLECTION_IMAGES[handle]) {
      return Promise.resolve(window.AMP_COLLECTION_IMAGES[handle]);
    }
    
    return fetch('/collections/' + handle + '.json')
      .then(function(res) { return res.ok ? res.json() : null; })
      .then(function(data) {
        var imageUrl = null;
        if (data && data.collection && data.collection.image && data.collection.image.src) {
          imageUrl = data.collection.image.src;
          window.AMP_COLLECTION_IMAGES[handle] = imageUrl;
        }
        return imageUrl;
      })
      .catch(function() { return null; });
  }

  /**
   * Transform the empty cart from text buttons to a visual grid with collection images.
   * Extracts collection handles from existing links and fetches collection images via AJAX.
   */
  function enhanceEmptyCart() {
    var buttonsContainer = document.querySelector('.empty-cart-collections-buttons');
    if (!buttonsContainer) return;
    if (buttonsContainer.getAttribute('data-amp-empty-enhanced') === '1') return;

    // Extract collection data from existing links
    var links = buttonsContainer.querySelectorAll('a');
    if (!links.length) return;

    var collections = [];
    for (var i = 0; i < links.length; i++) {
      var href = links[i].getAttribute('href') || '';
      var match = href.match(/\/collections\/([^/?#]+)/);
      if (match) {
        collections.push({
          handle: match[1],
          name: links[i].textContent.trim(),
          href: href
        });
      }
    }

    if (!collections.length) return;

    // Mark as enhanced to prevent re-processing
    buttonsContainer.setAttribute('data-amp-empty-enhanced', '1');

    // Hide original buttons
    buttonsContainer.style.display = 'none';

    // Hide the "Shop by category" header
    var shopHeader = document.querySelector('.empty-cart-collections-header');
    if (shopHeader) shopHeader.style.display = 'none';

    // Fetch all collection images in parallel, then render the grid
    var imagePromises = collections.map(function(col) {
      return fetchCollectionImage(col.handle);
    });

    Promise.all(imagePromises).then(function(images) {
      // Create the new grid container
      var gridContainer = document.createElement('div');
      gridContainer.className = 'amp-empty-cart-wrapper';

      // Add "YOUR CART IS EMPTY" heading
      var heading = document.createElement('h2');
      heading.className = 'amp-empty-cart-heading';
      heading.textContent = 'YOUR CART IS EMPTY';
      gridContainer.appendChild(heading);

      // Create grid
      var grid = document.createElement('div');
      grid.className = 'amp-empty-cart-grid';

      // Create tiles for each collection
      collections.forEach(function (col, index) {
        var tile = document.createElement('a');
        tile.href = col.href;
        tile.className = 'amp-empty-cart-tile';

        var imgWrapper = document.createElement('div');
        imgWrapper.className = 'amp-empty-cart-tile-img';

        var img = document.createElement('img');
        img.alt = col.name;
        img.onerror = function() { this.style.display = 'none'; };
        img.src = images[index] || '';

        imgWrapper.appendChild(img);

        var label = document.createElement('span');
        label.className = 'amp-empty-cart-tile-label';
        // The "/collections/all" tile should read "SHOP ALL" rather than the collection's own name.
        label.textContent = /\/collections\/all(?:[/?#]|$)/.test(col.href)
          ? 'SHOP ALL'
          : col.name.toUpperCase();

        tile.appendChild(imgWrapper);
        tile.appendChild(label);
        grid.appendChild(tile);
      });

      gridContainer.appendChild(grid);

      // Insert the grid before the buttons container
      if (buttonsContainer.parentNode) {
        buttonsContainer.parentNode.insertBefore(gridContainer, buttonsContainer);
      }
    });
  }

  /**
   * Update every progress bar and line item currently in the document.
   */
  function updateAll() {
    var bars = document.querySelectorAll(BAR_SELECTOR);
    for (var i = 0; i < bars.length; i++) updateBar(bars[i]);

    enhanceItems();
    wrapDiscountAccordion();
    enhanceUpsellItems();
    updateCheckoutButton();
    updateSubtotalLabel();
    enhanceEmptyCart();

    // Reconcile the upsells space reservation now that the DOM is settled:
    // if the new upsells are back, drop the reserve; if not, keep holding it.
    syncUpsellsReservation();
  }

  // Debounce so a burst of mutations from a single re-render only runs once.
  var pending = false;
  function scheduleUpdate() {
    // Reserve space synchronously the moment a re-render starts, before the
    // browser paints. If the upsells node is already detached we hold its
    // last-known height immediately; if it's still present we snapshot it so
    // we're ready the instant it vanishes mid-burst. Either way the footer
    // never gets a frame in which it can jump up.
    var upsells = getUpsells();
    if (upsells && upsells.offsetHeight > 0) {
      captureUpsellsHeight();
    } else if (lastUpsellsHeight > 0) {
      reserveUpsellsSpace();
    }

    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      updateAll();
    });
  }

  function start() {
    scheduleUpdate();

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        // React to nodes being added/removed (re-render) or the fill width
        // attribute changing (animated progress update).
        if (
          m.type === 'childList' ||
          (m.type === 'attributes' && m.target && m.target.classList &&
            m.target.classList.contains('rewards-progress'))
        ) {
          scheduleUpdate();
          return;
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
